import React from 'react';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';

/**
 * Reusable KPI card for the Dashboard.
 *
 * Props:
 *  - label:        top-left metric title (French).
 *  - value:        numeric value for the current period.
 *  - previous:     numeric value for the previous period (used for delta).
 *  - format:       'number' | 'percent' (default 'number').
 *  - Icon:         a lucide-react component reference.
 *  - iconColor:    hex string used both for the icon and its bg (@15% opacity).
 *  - inversedDelta:if true, a RISE is considered BAD (red) and a fall GOOD (green).
 *                  Applied to Sans Réponse / Retourné / Faux / Taux de Retour.
 *  - testId:       root data-testid.
 */
function fmtNumber(n) {
    if (n === null || n === undefined) return '0';
    return Number(n).toLocaleString('fr-FR');
}
function fmtPercent(n) {
    if (n === null || n === undefined) return '0%';
    return `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}%`;
}

/**
 * Delta % logic:
 *  - previous === 0 && current === 0 → { delta: 0, direction: 'flat' }
 *  - previous === 0 && current > 0   → direction 'up',   delta = 100 (client decision).
 *  - previous > 0                     → delta = (cur - prev) / prev * 100
 * We return { direction: 'up'|'down'|'flat', display: '+3,4%' }
 */
function computeDelta(current, previous) {
    const cur = Number(current || 0);
    const prev = Number(previous || 0);
    if (prev === 0 && cur === 0) return { direction: 'flat', display: '0%' };
    if (prev === 0) return { direction: 'up', display: '+100%' };
    const diff = ((cur - prev) / prev) * 100;
    const dir = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
    const sign = diff > 0 ? '+' : '';
    return { direction: dir, display: `${sign}${diff.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}%` };
}

export default function KpiCard({
    label,
    value,
    previous,
    format = 'number',
    Icon,
    iconColor = '#4B3FD8',
    inversedDelta = false,
    testId,
}) {
    const delta = computeDelta(value, previous);
    // Color mapping:
    //   Regular (inversedDelta=false): up=green, down=red, flat=grey.
    //   Inversed metrics (Sans Rep / Retour / Faux / Taux Retour): up=red, down=green.
    const isGood =
        (delta.direction === 'up' && !inversedDelta) ||
        (delta.direction === 'down' && inversedDelta);
    const isBad =
        (delta.direction === 'up' && inversedDelta) ||
        (delta.direction === 'down' && !inversedDelta);
    const deltaColor = delta.direction === 'flat'
        ? '#6B7280'
        : isGood
            ? '#10B981'
            : isBad
                ? '#EF4444'
                : '#6B7280';

    const ArrowIcon =
        delta.direction === 'up' ? ArrowUp : delta.direction === 'down' ? ArrowDown : Minus;

    const displayValue = format === 'percent' ? fmtPercent(value) : fmtNumber(value);

    return (
        <div
            data-testid={testId}
            className="bg-white rounded-xl border border-[#E5E7EB] p-4 shadow-[0_1px_2px_rgba(16,24,40,0.05)] flex flex-col gap-3 min-h-[132px]"
        >
            <div className="flex items-start justify-between">
                <div className="text-[13px] text-[#6B7280] font-medium leading-tight">
                    {label}
                </div>
                {Icon && (
                    <div
                        className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${iconColor}1A` /* 10% */ }}
                    >
                        <Icon size={18} strokeWidth={1.75} style={{ color: iconColor }} />
                    </div>
                )}
            </div>
            <div className="text-[28px] leading-none font-bold text-[#1A1D2E]" data-testid={testId ? `${testId}-value` : undefined}>
                {displayValue}
            </div>
            <div className="flex items-center gap-1.5 text-[12px] mt-auto">
                <span
                    className="inline-flex items-center gap-1 font-semibold"
                    style={{ color: deltaColor }}
                    data-testid={testId ? `${testId}-delta` : undefined}
                >
                    <ArrowIcon size={13} strokeWidth={2.25} />
                    {delta.display}
                </span>
                <span className="text-[#6B7280]">vs période précédente</span>
            </div>
        </div>
    );
}
